import { Link } from 'react-router-dom'
import { Key, Puzzle, Code, Blocks } from 'lucide-react'

export default function IntegrationsPage() {
  return (
    <div className="relative overflow-hidden">
      <div aria-hidden className="absolute inset-0 -z-30 bg-gradient-to-b from-emerald-50 via-white to-white dark:from-zinc-950 dark:via-zinc-900 dark:to-zinc-900" />
      <div aria-hidden className="absolute inset-x-0 top-0 -z-40 h-[320px] bg-[radial-gradient(circle_at_top,_rgba(16,185,129,0.25),_transparent_68%)]" />
      <div aria-hidden className="absolute inset-0 -z-20 bg-[radial-gradient(circle_at_15%_18%,rgba(236,72,153,0.14),transparent_60%),radial-gradient(circle_at_85%_18%,rgba(168,85,247,0.17),transparent_55%)] dark:bg-[radial-gradient(circle_at_12%_18%,rgba(236,72,153,0.12),transparent_55%),radial-gradient(circle_at_88%_20%,rgba(168,85,247,0.15),transparent_50%)]" />

      <div className="relative mx-auto max-w-[1600px] px-1 py-3 sm:px-6 sm:py-6">
        <div className="mb-6">
          <button
            onClick={() => window.location.href = '/config'}
            className="inline-flex items-center gap-2 rounded-full border border-zinc-200/70 dark:border-zinc-800 bg-white/80 dark:bg-zinc-900/70 px-4 py-2 text-sm font-medium text-zinc-700 dark:text-zinc-300 shadow-sm transition hover:bg-zinc-50 dark:hover:bg-zinc-800"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
            Retour
          </button>
        </div>

        <div className="mb-8">
          <h1 className="text-3xl font-bold text-zinc-900 dark:text-white">Intégrations</h1>
          <p className="mt-2 text-zinc-600 dark:text-zinc-400">Connectez Totemium à vos outils et plateformes</p>
        </div>

        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-2">
          <IntegrationTile
            icon={<Key className="h-8 w-8" />}
            title="Clé API"
            description="Générez votre clé API pour les intégrations externes (WordPress, CMS custom, etc.)"
            href="/integrations/api-key"
            color="sky"
          />

          <IntegrationTile
            icon={<Puzzle className="h-8 w-8" />}
            title="Plugin WordPress"
            description="Téléchargez et installez le plugin officiel Totemium pour LearnDash"
            href="/integrations/wordpress"
            color="purple"
          />

          <IntegrationTile
            icon={<Code className="h-8 w-8" />}
            title="Script embarqué"
            description="Intégrez Totemium dans n'importe quel site avec un simple snippet JavaScript"
            href="/integrations/iframe"
            color="orange"
          />

          <IntegrationTile
            icon={<Blocks className="h-8 w-8" />}
            title="Catalogue MCP"
            description="Connectez vos agents à des outils externes via le protocole MCP (GitHub, WowBooking, etc.)"
            href="/integrations/mcp"
            color="emerald"
          />
        </div>
      </div>
    </div>
  )
}

type IntegrationTileProps = {
  icon: React.ReactNode
  title: string
  description: string
  href: string
  color: 'sky' | 'purple' | 'orange' | 'emerald'
}

function IntegrationTile({ icon, title, description, href, color }: IntegrationTileProps) {
  const colorClasses = {
    sky: 'from-sky-500/20 to-sky-600/20 hover:from-sky-500/30 hover:to-sky-600/30 text-sky-600 dark:text-sky-400',
    purple: 'from-purple-500/20 to-purple-600/20 hover:from-purple-500/30 hover:to-purple-600/30 text-purple-600 dark:text-purple-400',
    orange: 'from-orange-500/20 to-orange-600/20 hover:from-orange-500/30 hover:to-orange-600/30 text-orange-600 dark:text-orange-400',
    emerald: 'from-emerald-500/20 to-emerald-600/20 hover:from-emerald-500/30 hover:to-emerald-600/30 text-emerald-600 dark:text-emerald-400',
  }

  return (
    <Link
      to={href}
      className="group relative overflow-hidden rounded-2xl border border-zinc-200/80 bg-white/95 p-6 shadow-md backdrop-blur transition hover:shadow-lg dark:border-zinc-800/70 dark:bg-zinc-900/85"
    >
      <div className={`absolute inset-0 bg-gradient-to-br opacity-0 transition group-hover:opacity-100 ${colorClasses[color]}`} />
      <div className="relative space-y-4">
        <div className={`inline-flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br ${colorClasses[color]}`}>
          {icon}
        </div>
        <div>
          <h3 className="text-lg font-semibold text-zinc-900 dark:text-white">{title}</h3>
          <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-400">{description}</p>
        </div>
        <div className="flex items-center gap-2 text-sm font-medium text-zinc-500 dark:text-zinc-400">
          Accéder
          <svg className="h-4 w-4 transition group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </div>
      </div>
    </Link>
  )
}
